// Recurring billing, proration, invoices and dunning.
//
// The teardown found failed-payment recovery to be near-universal and the
// single biggest driver of collected revenue, so retries are modelled as
// first-class state rather than left to the processor.

import { addDays, fromDateKey, toDateKey } from './recurrence.js';
import { allocateByRatio, multiply } from './money.js';

export const INVOICE_STATUS = {
  DRAFT: 'draft',
  OPEN: 'open',
  PAID: 'paid',
  PAST_DUE: 'past_due',
  UNCOLLECTIBLE: 'uncollectible',
  VOID: 'void',
};

export const INTERVALS = { WEEK: 'week', MONTH: 'month', YEAR: 'year' };

export function daysBetween(fromKey, toKey) {
  return Math.round((fromDateKey(toKey) - fromDateKey(fromKey)) / 86400000);
}

/** Add one billing interval to a date, clamping short months (Jan 31 -> Feb 28). */
export function advancePeriod(dateKey, interval, count = 1) {
  if (interval === INTERVALS.WEEK) return addDays(dateKey, 7 * count);

  const [y, m, d] = dateKey.split('-').map(Number);
  const months = interval === INTERVALS.YEAR ? 12 * count : count;
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return toDateKey(target);
}

/**
 * Charge for a partial period. A member joining on the 20th of a monthly cycle
 * pays for the days they actually get.
 */
export function prorate({ amountCents, periodStart, periodEnd, effectiveDate }) {
  const total = daysBetween(periodStart, periodEnd);
  if (total <= 0) return 0;
  const remaining = daysBetween(effectiveDate, periodEnd);
  if (remaining <= 0) return 0;
  if (remaining >= total) return amountCents;
  return multiply(amountCents, remaining / total);
}

/**
 * Switching plans mid-cycle: credit the unused part of the old plan, charge the
 * used-forward part of the new one. Returns the net amount due now (negative
 * means account credit).
 */
export function planChange({
  oldAmountCents, newAmountCents, periodStart, periodEnd, effectiveDate,
}) {
  const credit = prorate({ amountCents: oldAmountCents, periodStart, periodEnd, effectiveDate });
  const charge = prorate({ amountCents: newAmountCents, periodStart, periodEnd, effectiveDate });
  return { creditCents: credit, chargeCents: charge, netCents: charge - credit };
}

/**
 * Build an invoice from lines. Tax is applied per line and allocated so the
 * rounded parts always sum to the rounded total.
 *
 * lines: [{ description, amountCents, taxable, quantity? }]
 */
export function buildInvoice({ lines, taxRateBps = 0, discountCents = 0, currency = 'USD' }) {
  const priced = lines.map((l) => ({
    ...l,
    quantity: l.quantity ?? 1,
    lineTotalCents: l.amountCents * (l.quantity ?? 1),
  }));

  const subtotal = priced.reduce((a, l) => a + l.lineTotalCents, 0);

  // Spread any discount across lines by value, so per-line tax stays honest.
  const discounts = discountCents > 0
    ? allocateByRatio(Math.min(discountCents, subtotal), priced.map((l) => l.lineTotalCents))
    : priced.map(() => 0);

  const withDiscount = priced.map((l, i) => ({
    ...l,
    discountCents: discounts[i],
    netCents: l.lineTotalCents - discounts[i],
  }));

  const taxable = withDiscount.reduce((a, l) => a + (l.taxable ? l.netCents : 0), 0);
  const taxCents = multiply(taxable, taxRateBps / 10000);
  const netSubtotal = withDiscount.reduce((a, l) => a + l.netCents, 0);

  return {
    currency,
    lines: withDiscount,
    subtotalCents: subtotal,
    discountCents: subtotal - netSubtotal,
    taxCents,
    totalCents: netSubtotal + taxCents,
    status: INVOICE_STATUS.OPEN,
  };
}

/**
 * Retry cadence for a failed payment. Spacing is deliberate: two quick retries
 * catch temporary declines and insufficient funds around payday, then it slows
 * before handing off.
 */
export const DUNNING_SCHEDULE = [
  { attempt: 1, afterDays: 1, channel: 'email' },
  { attempt: 2, afterDays: 3, channel: 'email' },
  { attempt: 3, afterDays: 7, channel: 'sms' },
  { attempt: 4, afterDays: 14, channel: 'sms' },
  { attempt: 5, afterDays: 25, channel: 'staff_task' },
];

export const DUNNING_GIVE_UP_DAYS = 30;

/** Display names for the channels above. "sms" should read as SMS, not Sms. */
export const CHANNEL_LABEL = {
  email: 'Email',
  sms: 'SMS',
  staff_task: 'Staff task',
};

/** What to do next for a past-due invoice. */
export function nextDunningStep({ failedOnKey, attemptsMade, todayKey }) {
  // invoices.failed_on is nullable, and an invoice can reach past_due without
  // a recorded failure (manual status change, an import, a processor webhook
  // that arrived without one). Without this guard daysBetween throws
  // RangeError: Invalid time value, which takes down the whole Billing screen
  // rather than the one row that is malformed.
  if (!failedOnKey) {
    return {
      action: 'needs_review',
      reason: 'No failure date recorded, so the retry schedule cannot be computed.',
    };
  }

  const step = DUNNING_SCHEDULE[attemptsMade];
  const age = daysBetween(failedOnKey, todayKey);

  if (!step || age >= DUNNING_GIVE_UP_DAYS) {
    return { action: 'give_up', status: INVOICE_STATUS.UNCOLLECTIBLE, ageDays: age };
  }

  const dueOn = addDays(failedOnKey, step.afterDays);
  if (todayKey < dueOn) return { action: 'wait', nextAttemptOn: dueOn, ...step };
  return { action: 'retry', dueOn, ...step };
}

/**
 * Project the next N billing dates for a membership.
 *
 * Every date is computed from the ANCHOR, never from the previous result. This
 * is load-bearing, not a style choice: advancePeriod clamps short months, so
 * stepping from each result in turn drags a Jan-31 member to Feb 28 and then
 * bills them on the 28th forever. Anchoring restores the 31st in March, which
 * is both what Stripe does and what an operator means by "billed on the 31st".
 *
 * The same rule binds anything that advances a real billing period later: take
 * the step from `startDate` with a count, never from `currentPeriodEnd`.
 */
export function upcomingCharges({ startDate, interval, amountCents, count = 3, cancelAt }) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const date = i === 0 ? startDate : advancePeriod(startDate, interval, i);
    if (cancelAt && date >= cancelAt) break;
    out.push({ date, amountCents });
  }
  return out;
}

/** Monthly recurring revenue, normalised across intervals. */
export function mrrCents(memberships) {
  return memberships
    .filter((m) => m.status === 'active')
    .reduce((sum, m) => {
      if (m.interval === INTERVALS.WEEK) return sum + multiply(m.amountCents, 52 / 12);
      if (m.interval === INTERVALS.YEAR) return sum + Math.round(m.amountCents / 12);
      return sum + m.amountCents;
    }, 0);
}
